import * as React from 'react';
import * as moment from 'moment-business-days';
import { push } from 'react-router-redux';
import { connect } from 'react-redux';
import { Button, Card, Notification, Tag, Loader } from '../';
import EXPIRATIONS from '../../data/expirations';
import { getBookingsWithPaymentStatus } from '../../redux/actions/bookings';
import PAGINATE from '../../data/paginate';

interface Props {
  dispatch: Function;
}

interface State {
  isLoading: boolean;
  bookings: Array<any>;
  errorMessage: string | boolean;
}

class PreAuthBookingTable extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      bookings: [],
      errorMessage: false,
    };
    this.handleUpdate = this.handleUpdate.bind(this);
    this.renderItem = this.renderItem.bind(this);
  }

  async componentDidMount() {
    this.handleUpdate();
  }

  /**
   * Fetch bookings with pre-auth status and calculate days left
   */
  async handleUpdate() {
    this.setState({ isLoading: true });
    const response = await this.props.dispatch(
      getBookingsWithPaymentStatus(
        4,
        0,
        undefined,
        'pre_auth_expiry_date',
        undefined,
        undefined,
        0,
      ),
    );
    if (response.error) {
      this.setState({ errorMessage: response.error });
      return;
    }

    // Retrieve the pre auth expiration dates needed and sort by the
    // end after pre auth expiry
    const bookings = response.data
      .map((booking) => {
        const daysAgo =
          moment().businessDiff(moment(booking.pre_auth_expiry_date)) - 1;
        const dayFinish = moment(booking.pre_auth_expiry_date).businessAdd(
          EXPIRATIONS.PRE_AUTH,
        );
        const preAuthExpired = daysAgo > EXPIRATIONS.PRE_AUTH;
        booking.daysAgo = daysAgo;
        booking.dayFinish = dayFinish;
        booking.preAuthExpired = preAuthExpired;
        return booking;
      })
      .sort((a, b) => {
        if (
          moment(b.pre_auth_expiry_date).businessDiff(moment(b.end)) >
          EXPIRATIONS.PRE_AUTH
        ) {
          return 1;
        }
        if (
          moment(a.pre_auth_expiry_date).businessDiff(moment(a.end)) >
          EXPIRATIONS.PRE_AUTH
        ) {
          return -1;
        }
        return 0;
      });
    this.setState({ bookings, isLoading: false });
  }

  /**
   * Render an individual booking
   * @param {Object} booking
   */
  renderItem(booking) {
    const outstanding = Number(booking.total_cost - booking.total_paid).toFixed(
      2,
    );
    const highlight =
      booking.preAuthExpired || moment(booking.end).isAfter(booking.dayFinish);
    const expiryDate = moment.duration(
      Math.abs(booking.daysAgo - EXPIRATIONS.PRE_AUTH),
      'days',
    );
    return (
      <tr
        key={booking.id}
        onClick={() =>
          this.props.dispatch(push(`/payments/pre-auth/${booking.id}`))
        }
        className={highlight ? 'expired' : ''}
      >
        <td>
          <div className="subtitle is-marginless">
            &pound;{Number(outstanding).toFixed(2)}
          </div>
        </td>
        <td>
          <div className="subtitle is-marginless">
            &pound;{Number(booking.total_paid).toFixed(2)}
          </div>
        </td>
        <td>
          <span className="is-pulled-right">
            <Tag type="default">{booking.invoiceId}</Tag>
          </span>
          {booking.contact_name || 'N/A'}
        </td>
        <td>{moment(booking.end).format('Do MMMM')}</td>
        <td>
          <span className="is-pulled-right">
            <Tag type="default">
              {expiryDate.humanize()}{' '}
              {booking.preAuthExpired ? 'overdue' : 'left'}
            </Tag>
          </span>
          {booking.dayFinish.format('Do MMMM')}
        </td>
      </tr>
    );
  }

  render() {
    if (this.state.errorMessage) {
      return (
        <Notification type="danger">{this.state.errorMessage}</Notification>
      );
    }
    return (
      <div>
        <Card list>
          {this.state.isLoading && <Loader fullwidth />}
          {!this.state.isLoading && (
            <table className="pre-auth-table">
              <thead>
                <tr>
                  <th>Total Due</th>
                  <th>Pre-Auth Charged</th>
                  <th>Customer</th>
                  <th>Rental End Date</th>
                  <th>Pre-Auth Expiry Date</th>
                </tr>
              </thead>
              <tbody>
                {this.state.bookings.map((booking, index) => {
                  if (index >= PAGINATE.payments.MAX) {
                    return null;
                  }
                  return this.renderItem(booking);
                })}
              </tbody>
            </table>
          )}
          {!this.state.isLoading && this.state.bookings.length === 0 && (
            <Notification type="default">Nothing found</Notification>
          )}
        </Card>
      </div>
    );
  }
}

export default connect()(PreAuthBookingTable);
